"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowUpRightOutline18, IconCircleCheckOutline18, IconCode2Outline18 } from "nucleo-ui-outline-18";
import { useChainInfo } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { getChainIconUrl } from "@/app/api/chains/chainIcons";
import { Badge } from "@/components/ui/badge";
import { RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TokenIcon } from "@/components/ui/token-icon";
import { TokenWithChainIcon } from "@/components/ui/token-with-chain-icon";
import { findMatchingOrganizationWallet } from "@/components/ui/workflow-step-row/components/InlineAddressLink";
import { useB3Profile } from "@/hooks/useB3Profile";
import { formatAddress, formatUsername } from "@/lib/utils";
import type { OrganizationWallet } from "@/types/wallet";
import { highlight, languages } from "prismjs";
import "prismjs/components/prism-json";
import { type ReactNode, useMemo, useState } from "react";

// ============================================================================
// Shared Row Wrapper — eliminates boilerplate across all row components
// ============================================================================

export interface PickerRowProps {
  data: Record<string, unknown>;
  value: string;
  selected: boolean;
  onSelect: (v: string) => void;
  /** Flex alignment for the content area: "center" (default) or "start" for multi-line */
  align?: "center" | "start";
  children: ReactNode;
}
export type FarcasterReactionType = "like" | "recast";
export const SAMPLE_FARCASTER_REACTOR = {
  object: "user",
  fid: 3,
  username: "dwr.eth",
  display_name: "Dan Romero",
  pfp_url: "",
  follower_count: 500000,
  following_count: 3000
};
export function buildFarcasterReactionPayload(castEvent: Record<string, unknown>, reactionType: FarcasterReactionType): Record<string, unknown> {
  const timestamp = new Date().toISOString();
  const body = ((castEvent.body as Record<string, unknown> | undefined)?.data ?? {}) as Record<string, unknown>;
  const author = body.author as Record<string, unknown> | undefined ?? {};
  const reactions = body.reactions as Record<string, unknown> | undefined ?? {};
  return {
    triggeredAt: timestamp,
    eventType: "reaction.created",
    body: {
      data: {
        object: "reaction",
        reaction_type: reactionType,
        reaction_timestamp: timestamp,
        user: SAMPLE_FARCASTER_REACTOR,
        cast: {
          object: "cast",
          hash: String(body.hash ?? ""),
          text: String(body.text ?? ""),
          timestamp: String(body.timestamp ?? timestamp),
          author: {
            object: "user",
            fid: Number(author.fid ?? 0),
            username: String(author.username ?? ""),
            display_name: String(author.display_name ?? ""),
            pfp_url: String(author.pfp_url ?? "")
          },
          reactions: {
            likes_count: Number(reactions.likes_count ?? reactions.likesCount ?? 0),
            recasts_count: Number(reactions.recasts_count ?? reactions.recastsCount ?? 0)
          }
        }
      }
    }
  };
}

/** Highlight JSON with inline colors so it works without global Prism theme classes */
function highlightJson(json: string): string {
  const html = highlight(json, languages.json, "json");
  // Apply colors directly to Prism token spans
  return html.replace(/class="token property"/g, 'style="color:#a855f7"').replace(/class="token string"/g, 'style="color:#059669"').replace(/class="token number"/g, 'style="color:#ea580c"').replace(/class="token boolean"/g, 'style="color:#ea580c"').replace(/class="token null keyword"/g, 'style="color:#ea580c"').replace(/class="token punctuation"/g, 'style="color:#71717a"').replace(/class="token operator"/g, 'style="color:#71717a"');
}

/** Generic row with radio button, clickable content, JSON toggle, and raw preview */
export function PickerRow(t0) {
  const $ = _c(34);
  const {
    data,
    value,
    selected,
    onSelect,
    align: t1,
    children
  } = t0;
  const align = t1 === undefined ? "center" : t1;
  const [showRaw, setShowRaw] = useState(false);
  let t2;
  let t3;
  if ($[0] !== data || $[1] !== showRaw) {
    t3 = showRaw ? highlightJson(JSON.stringify(data, null, 2)) : "";
    $[0] = data;
    $[1] = showRaw;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  t2 = t3;
  const jsonHtml = t2;
  const t4 = `flex ${align === "start" ? "items-start" : "items-center"} gap-2.5 py-2`;
  let t5;
  if ($[3] !== onSelect || $[4] !== value) {
    t5 = () => onSelect(value);
    $[3] = onSelect;
    $[4] = value;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = `flex-shrink-0 ${align === "start" ? "mt-1" : ""}`;
  let t7;
  if ($[6] !== selected || $[7] !== t5 || $[8] !== t6 || $[9] !== value) {
    t7 = <RadioGroupItem value={value} checked={selected} onClick={t5} className={t6} />;
    $[6] = selected;
    $[7] = t5;
    $[8] = t6;
    $[9] = value;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== onSelect || $[12] !== value) {
    t8 = () => onSelect(value);
    $[11] = onSelect;
    $[12] = value;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== children || $[15] !== t8) {
    t9 = <button type="button" onClick={t8} className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">{children}</button>;
    $[14] = children;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== showRaw) {
    t10 = e => {
      e.stopPropagation();
      setShowRaw(!showRaw);
    };
    $[17] = showRaw;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = showRaw ? "Hide JSON" : "View JSON";
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <IconCode2Outline18 className="h-3 w-3" />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t11) {
    t13 = <button type="button" onClick={t10} className="hover:bg-ds-surface-hover hover:text-ds-text-muted dark:hover:bg-ds-neutral-800 dark:hover:text-ds-text-disabled flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-zinc-300 transition-colors" title={t11}>{t12}</button>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13 || $[24] !== t4 || $[25] !== t7 || $[26] !== t9) {
    t14 = <div className={t4}>{t7}{t9}{t13}</div>;
    $[23] = t13;
    $[24] = t4;
    $[25] = t7;
    $[26] = t9;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== jsonHtml || $[29] !== showRaw) {
    t15 = showRaw && <pre className="text-ds-text-secondary dark:bg-ds-neutral-900 text-2xs mb-1 ml-8 max-h-32 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-zinc-50 p-2 font-mono leading-relaxed" dangerouslySetInnerHTML={{
      __html: jsonHtml
    }} />;
    $[28] = jsonHtml;
    $[29] = showRaw;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t14 || $[32] !== t15) {
    t16 = <div className="min-w-0">{t14}{t15}</div>;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  return t16;
}

/** Format amount with known decimals */
function formatWithDecimals(raw: string | number, decimals: number): string {
  const n = Number(raw);
  if (isNaN(n) || n === 0) return "0";
  const converted = n / 10 ** decimals;
  return converted.toLocaleString(undefined, {
    maximumFractionDigits: Math.min(decimals, 6)
  });
}

/** Format a raw amount string — tries common decimal values, falls back to compact notation */
function formatAmount(raw: string | number): string {
  const str = String(raw);
  if (!str || str === "0") return "0";
  if (str.includes(".")) return str; // Already formatted

  const n = Number(str);
  if (isNaN(n)) return str;

  // Try common decimals: 6 (USDC/USDT), 8 (WBTC), 18 (ETH/ERC20)
  for (const d of [6, 8, 18]) {
    const converted = n / 10 ** d;
    if (converted >= 0.0001 && converted < 1_000_000_000) {
      return converted.toLocaleString(undefined, {
        maximumFractionDigits: Math.min(d, 6)
      });
    }
  }

  // Small values that don't fit any decimal — show as-is
  if (n < 1_000_000) return n.toLocaleString();

  // Fallback: compact notation
  return n.toLocaleString(undefined, {
    notation: "compact",
    maximumFractionDigits: 2
  });
}

/** Compact row for EVM transfer events — shows direction, amount, token, chain, address */
export function TransferAddressLabel(t0) {
  const $ = _c(24);
  const {
    address,
    wallets,
    className
  } = t0;
  let t1;
  if ($[0] !== address || $[1] !== wallets) {
    t1 = findMatchingOrganizationWallet(address, wallets);
    $[0] = address;
    $[1] = wallets;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const matchingWallet = t1;
  const t2 = Boolean(address && !matchingWallet);
  let t3;
  if ($[3] !== address || $[4] !== t2) {
    t3 = {
      addressOrName: address,
      enabled: t2
    };
    $[3] = address;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: profile
  } = useB3Profile(t3);
  let t4;
  if ($[6] !== address) {
    t4 = formatAddress(address);
    $[6] = address;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const truncatedAddress = t4;
  const t5 = profile?.displayName || profile?.name;
  let t6;
  if ($[8] !== t5) {
    t6 = formatUsername(t5);
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const profileName = t6;
  if (matchingWallet) {
    const t7 = `${matchingWallet.name} (${matchingWallet.id}) - ${matchingWallet.address}`;
    let t8;
    if ($[10] !== className || $[11] !== matchingWallet.name || $[12] !== t7 || $[13] !== truncatedAddress) {
      t8 = <span className={className} title={t7}>{matchingWallet.name} ({truncatedAddress})</span>;
      $[10] = className;
      $[11] = matchingWallet.name;
      $[12] = t7;
      $[13] = truncatedAddress;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    return t8;
  }
  if (profileName) {
    const t7 = `${profileName} - ${address}`;
    let t8;
    if ($[15] !== className || $[16] !== profileName || $[17] !== t7 || $[18] !== truncatedAddress) {
      t8 = <span className={className} title={t7}>{profileName} ({truncatedAddress})</span>;
      $[15] = className;
      $[16] = profileName;
      $[17] = t7;
      $[18] = truncatedAddress;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    return t8;
  }
  let t7;
  if ($[20] !== address || $[21] !== className || $[22] !== truncatedAddress) {
    t7 = <span className={className} title={address}>{truncatedAddress}</span>;
    $[20] = address;
    $[21] = className;
    $[22] = truncatedAddress;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}
export function TransferRow(t0) {
  const $ = _c(46);
  const {
    data,
    value,
    selected,
    onSelect,
    triggerType,
    chainId,
    chainName,
    chainIcon,
    tokenSymbol,
    tokenDecimals,
    wallets
  } = t0;
  let t1;
  if ($[0] !== data.params) {
    t1 = data.params ?? {};
    $[0] = data.params;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const params = t1 as Record<string, unknown>;
  let t2;
  if ($[2] !== data.raw) {
    t2 = data.raw ?? {};
    $[2] = data.raw;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const raw = t2 as Record<string, unknown>;
  const from = String(params.from ?? data.from ?? "");
  const to = String(params.to ?? data.to ?? "");
  const amount = String(params.value ?? data.value ?? "0");
  const symbol = tokenSymbol ?? data._symbol as string | undefined;
  const dataDecimals = data._decimals as number | undefined;
  const effectiveDecimals = tokenDecimals ?? dataDecimals;
  const tokenAddr = String(raw.address ?? "");
  const isReceive = triggerType.includes("receive");
  const counterpartyAddress = isReceive ? from : to;
  let t3;
  if ($[4] !== amount || $[5] !== effectiveDecimals) {
    t3 = effectiveDecimals != null ? formatWithDecimals(amount, effectiveDecimals) : formatAmount(amount);
    $[4] = amount;
    $[5] = effectiveDecimals;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const formattedAmount = t3;
  let t4;
  if ($[7] !== symbol || $[8] !== tokenAddr) {
    t4 = symbol ?? (tokenAddr ? formatAddress(tokenAddr) : "tokens");
    $[7] = symbol;
    $[8] = tokenAddr;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const tokenLabel = t4;
  const t5 = tokenAddr || undefined;
  let t6;
  if ($[10] !== chainId || $[11] !== t5 || $[12] !== tokenLabel) {
    t6 = <TokenIcon symbol={tokenLabel} chainId={chainId} tokenAddress={t5} size={28} className="rounded-full" />;
    $[10] = chainId;
    $[11] = t5;
    $[12] = tokenLabel;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== chainIcon || $[15] !== chainName) {
    t7 = chainIcon ? <img src={chainIcon} alt={chainName || "Network"} className="dark:border-ds-neutral-900 bg-ds-surface-card absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white" /> : null;
    $[14] = chainIcon;
    $[15] = chainName;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="relative mt-0.5 flex-shrink-0">{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== formattedAmount) {
    t9 = <span className="text-ds-text-primary text-sm font-semibold tabular-nums">{formattedAmount}</span>;
    $[20] = formattedAmount;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== tokenLabel) {
    t10 = <span className="text-ds-text-muted min-w-0 truncate text-sm">{tokenLabel}</span>;
    $[22] = tokenLabel;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = <div className="flex min-w-0 items-baseline gap-2">{t9}{t10}</div>;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const t12 = isReceive ? "from" : "to";
  let t13;
  if ($[27] !== counterpartyAddress || $[28] !== wallets) {
    t13 = <TransferAddressLabel address={counterpartyAddress} wallets={wallets} className="inline" />;
    $[27] = counterpartyAddress;
    $[28] = wallets;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = chainName ? ` on ${chainName}` : "";
  let t15;
  if ($[30] !== t12 || $[31] !== t13 || $[32] !== t14) {
    t15 = <div className="text-ds-text-disabled dark:text-ds-text-muted mt-0.5 min-w-0 truncate text-xs"><span>{t12}{" "}{t13}{t14}</span></div>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t11 || $[35] !== t15) {
    t16 = <div className="min-w-0 flex-1">{t11}{t15}</div>;
    $[34] = t11;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t16 || $[38] !== t8) {
    t17 = <div className="flex min-w-0 items-start gap-3">{t8}{t16}</div>;
    $[37] = t16;
    $[38] = t8;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== data || $[41] !== onSelect || $[42] !== selected || $[43] !== t17 || $[44] !== value) {
    t18 = <PickerRow data={data} value={value} selected={selected} onSelect={onSelect}>{t17}</PickerRow>;
    $[40] = data;
    $[41] = onSelect;
    $[42] = selected;
    $[43] = t17;
    $[44] = value;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  return t18;
}

/** Compact row for Farcaster cast events */
export function CastRow(props) {
  const $ = _c(25);
  const {
    data
  } = props;
  const text = data._text as string ?? "";
  const author = data._authorDisplayName as string || data._authorUsername as string || "Unknown";
  const username = data._authorUsername as string ?? "";
  const likes = data._likesCount as number ?? 0;
  const recasts = data._recastsCount as number ?? 0;
  const body = data.body as Record<string, unknown> | undefined ?? undefined;
  const bodyData = body?.data as Record<string, unknown> | undefined ?? undefined;
  const bodyAuthor = (bodyData?.author ?? body?.author) as Record<string, unknown> | undefined ?? undefined;
  const pfpUrl = data._authorPfpUrl as string || bodyAuthor?.pfp_url as string | undefined;
  let t0;
  if ($[0] !== author || $[1] !== pfpUrl) {
    t0 = pfpUrl ? <img src={pfpUrl} alt={author} className="h-8 w-8 flex-shrink-0 rounded-full object-cover" /> : <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-medium text-violet-600 dark:bg-violet-950/60 dark:text-violet-300">{author.charAt(0).toUpperCase()}</div>;
    $[0] = author;
    $[1] = pfpUrl;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== author) {
    t1 = <span className="label-xs-medium">{author}</span>;
    $[3] = author;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== username) {
    t2 = username && <span className="text-ds-text-disabled dark:text-ds-text-muted text-xs">@{username}</span>;
    $[5] = username;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <div className="flex items-center gap-1.5">{t1}{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== text) {
    t4 = <span className="text-ds-text-primary mt-0.5 line-clamp-2 block text-sm">{text}</span>;
    $[10] = text;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== likes || $[13] !== recasts) {
    t5 = (likes > 0 || recasts > 0) && <span className="text-ds-text-disabled dark:text-ds-text-muted text-2xs mt-0.5 block tabular-nums">{likes > 0 && `${likes} like${likes !== 1 ? "s" : ""}`}{likes > 0 && recasts > 0 && " \xB7 "}{recasts > 0 && `${recasts} recast${recasts !== 1 ? "s" : ""}`}</span>;
    $[12] = likes;
    $[13] = recasts;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = <div className="min-w-0 flex-1">{t3}{t4}{t5}</div>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t0 || $[20] !== t6) {
    t7 = <div className="flex items-start gap-2.5">{t0}{t6}</div>;
    $[19] = t0;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== props || $[23] !== t7) {
    t8 = <PickerRow {...props} align="start">{t7}</PickerRow>;
    $[22] = props;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}

/** Compact row for Farcaster follower events */
export function FollowerRow(props) {
  const $ = _c(17);
  const {
    data
  } = props;
  const displayName = data._authorDisplayName as string || data._authorUsername as string || "Unknown";
  const username = data._authorUsername as string ?? "";
  const bodyUser = (data.body as Record<string, unknown>)?.user as Record<string, unknown> | undefined;
  const pfpUrl = data._authorPfpUrl as string || bodyUser?.pfp_url as string | undefined;
  const followers = data._followerCount as number ?? 0;
  let t0;
  if ($[0] !== displayName || $[1] !== pfpUrl) {
    t0 = pfpUrl ? <img src={pfpUrl} alt={displayName} className="h-5 w-5 flex-shrink-0 rounded-full object-cover" /> : <div className="bg-ds-neutral-200 text-ds-text-muted dark:bg-ds-neutral-700 text-3xs flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full font-medium">{displayName.charAt(0).toUpperCase()}</div>;
    $[0] = displayName;
    $[1] = pfpUrl;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== displayName) {
    t1 = <span className="text-ds-text-primary text-sm font-medium">{displayName}</span>;
    $[3] = displayName;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== username) {
    t2 = username && <span className="text-ds-text-disabled dark:text-ds-text-muted text-xs">@{username}</span>;
    $[5] = username;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== followers) {
    t3 = followers > 0 && <span className="text-ds-text-disabled dark:text-ds-text-muted ml-auto text-xs tabular-nums">{followers.toLocaleString()} followers</span>;
    $[7] = followers;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t0 || $[10] !== t1 || $[11] !== t2 || $[12] !== t3) {
    t4 = <div className="flex items-center gap-2">{t0}{t1}{t2}{t3}</div>;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== props || $[15] !== t4) {
    t5 = <PickerRow {...props}>{t4}</PickerRow>;
    $[14] = props;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}

/** Compact row for evm-log events — shows decoded params with formatted addresses */
export function EvmLogRow(props) {
  const $ = _c(18);
  const {
    data,
    explorerUrl
  } = props;
  let t0;
  if ($[0] !== data.params) {
    t0 = data.params ?? {};
    $[0] = data.params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const params = t0 as Record<string, unknown>;
  let t1;
  if ($[2] !== data.raw) {
    t1 = data.raw ?? {};
    $[2] = data.raw;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const raw = t1 as Record<string, unknown>;
  const decodeError = typeof data.decodeError === "string" ? data.decodeError : undefined;
  const txHash = String(raw.transactionHash ?? "");
  let T0;
  let t2;
  let t3;
  if ($[4] !== decodeError || $[5] !== params || $[6] !== props) {
    const paramEntries = Object.entries(params).filter(_temp);
    T0 = PickerRow;
    t2 = props;
    t3 = decodeError ? <div className="text-xs text-amber-600 dark:text-amber-400">ABI mismatch: {decodeError.length > 80 ? `${decodeError.slice(0, 80)}…` : decodeError}</div> : <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">{paramEntries.map(_temp2)}</div>;
    $[4] = decodeError;
    $[5] = params;
    $[6] = props;
    $[7] = T0;
    $[8] = t2;
    $[9] = t3;
  } else {
    T0 = $[7];
    t2 = $[8];
    t3 = $[9];
  }
  let t4;
  if ($[10] !== explorerUrl || $[11] !== txHash) {
    t4 = txHash && (explorerUrl ? <a href={`${explorerUrl}/tx/${txHash}`} target="_blank" rel="noopener noreferrer" onClick={_temp3} className="micro-label inline-flex items-center gap-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"><span>View transaction: {formatAddress(txHash)}</span><IconArrowUpRightOutline18 className="h-3 w-3" /></a> : <span className="text-ds-text-disabled dark:text-ds-text-muted text-2xs">tx {formatAddress(txHash)}</span>);
    $[10] = explorerUrl;
    $[11] = txHash;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== T0 || $[14] !== t2 || $[15] !== t3 || $[16] !== t4) {
    t5 = <T0 {...t2}>{t3}{t4}</T0>;
    $[13] = T0;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}

/** Compact row for Polymarket bet/position events */
function _temp3(e) {
  return e.stopPropagation();
}
function _temp2(t0) {
  const [key, val] = t0;
  const str = String(val);
  const isAddr = str.startsWith("0x") && str.length === 42;
  return <span key={key} className="text-ds-text-subtle text-xs"><span className="text-ds-text-disabled dark:text-ds-text-muted">{key}</span>{" "}<span className="font-medium tabular-nums">{isAddr ? formatAddress(str) : str.length > 20 ? `${str.slice(0, 16)}...` : str}</span></span>;
}
function _temp(t0) {
  const [, v] = t0;
  return v != null;
}
export function PolymarketRow(props) {
  const $ = _c(32);
  const {
    data
  } = props;
  const question = data._question as string ?? "";
  const outcome = data._outcome as string ?? "";
  const size = data._size as number ?? 0;
  const price = data._price as number ?? 0;
  const pnl = data._pnl as number ?? 0;
  const icon = data._icon as string ?? "";
  let t0;
  if ($[0] !== icon) {
    t0 = icon && <img src={icon} alt="" className="h-8 w-8 flex-shrink-0 rounded object-cover" />;
    $[0] = icon;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== question) {
    t1 = <span className="text-ds-text-primary line-clamp-1 text-sm">{question}</span>;
    $[2] = question;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== outcome) {
    t2 = <span className="font-medium">{outcome}</span>;
    $[4] = outcome;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== size) {
    t3 = size.toLocaleString(undefined, {
      maximumFractionDigits: 1
    });
    $[6] = size;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = price * 100;
  let t5;
  if ($[8] !== t4) {
    t5 = t4.toFixed(0);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = <span>{t3} shares @ {t5}¢</span>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = pnl >= 0 ? "text-emerald-500" : "text-red-500";
  const t8 = pnl >= 0 ? "+" : "";
  let t9;
  if ($[13] !== pnl) {
    t9 = pnl.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    $[13] = pnl;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t10 = <span className={t7}>{t8}{t9} USD</span>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t2 || $[21] !== t6) {
    t11 = <div className="text-ds-text-muted flex items-center gap-3 text-xs tabular-nums">{t2}{t6}{t10}</div>;
    $[19] = t10;
    $[20] = t2;
    $[21] = t6;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t1 || $[24] !== t11) {
    t12 = <div className="min-w-0 flex-1">{t1}{t11}</div>;
    $[23] = t1;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t0 || $[27] !== t12) {
    t13 = <div className="flex gap-2.5">{t0}{t12}</div>;
    $[26] = t0;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== props || $[30] !== t13) {
    t14 = <PickerRow {...props} align="start">{t13}</PickerRow>;
    $[29] = props;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}

/** Compact row for Polymarket market-close events — question + winning outcome */
export function MarketCloseRow(props) {
  const $ = _c(18);
  const {
    data
  } = props;
  const question = data._question as string ?? "";
  const winningOutcome = data._winningOutcome as string ?? "";
  const icon = data._icon as string ?? "";
  let t0;
  if ($[0] !== icon) {
    t0 = icon && <img src={icon} alt="" className="h-8 w-8 flex-shrink-0 rounded object-cover" />;
    $[0] = icon;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== question) {
    t1 = <span className="text-ds-text-primary line-clamp-1 text-sm">{question}</span>;
    $[2] = question;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="label label-xs">Resolved</span>;
    t3 = <span className="label label-xs dark:text-ds-text-subtle text-zinc-300" aria-hidden="true">·</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  const t4 = `label label-xs ${winningOutcome.toLowerCase() === "no" ? "text-red-500 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}`;
  let t5;
  if ($[6] !== t4 || $[7] !== winningOutcome) {
    t5 = <div className="flex items-center gap-1.5">{t2}{t3}<span className={t4}>{winningOutcome}</span></div>;
    $[6] = t4;
    $[7] = winningOutcome;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t5) {
    t6 = <div className="min-w-0 flex-1">{t1}{t5}</div>;
    $[9] = t1;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t0 || $[13] !== t6) {
    t7 = <div className="flex gap-2.5">{t0}{t6}</div>;
    $[12] = t0;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== props || $[16] !== t7) {
    t8 = <PickerRow {...props} align="start">{t7}</PickerRow>;
    $[15] = props;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}

/** Compact row for Polymarket new-market events — question + outcomes */
export function NewMarketRow(props) {
  const $ = _c(21);
  const {
    data
  } = props;
  const question = data._question as string ?? "";
  const eventTitle = data._eventTitle as string ?? "";
  const outcomes = data._outcomes as string ?? "";
  const icon = data._icon as string ?? "";
  let t0;
  if ($[0] !== icon) {
    t0 = icon && <img src={icon} alt="" className="h-8 w-8 flex-shrink-0 rounded object-cover" />;
    $[0] = icon;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== question) {
    t1 = <span className="text-ds-text-primary line-clamp-1 text-sm">{question}</span>;
    $[2] = question;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== eventTitle || $[5] !== question) {
    t2 = eventTitle && eventTitle !== question && <span className="truncate">{eventTitle}</span>;
    $[4] = eventTitle;
    $[5] = question;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== outcomes) {
    t3 = outcomes && <span className="truncate">{outcomes}</span>;
    $[7] = outcomes;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <div className="micro-label text-ds-text-disabled dark:text-ds-text-muted flex min-w-0 items-center gap-3 text-xs">{t2}{t3}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t1 || $[13] !== t4) {
    t5 = <div className="min-w-0 flex-1">{t1}{t4}</div>;
    $[12] = t1;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t0 || $[16] !== t5) {
    t6 = <div className="flex items-center gap-3">{t0}{t5}</div>;
    $[15] = t0;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== props || $[19] !== t6) {
    t7 = <PickerRow {...props} align="start">{t6}</PickerRow>;
    $[18] = props;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}

/** Compact row for Polymarket trade events — two-line layout like PolymarketRow */
export function TradeRow(props) {
  const $ = _c(36);
  const {
    data
  } = props;
  const side = data._side as string ?? "";
  const outcome = data._outcome as string ?? "";
  const size = data._size as number ?? 0;
  const price = data._price as number ?? 0;
  let t0;
  if ($[0] !== data.trade) {
    t0 = data.trade ?? {};
    $[0] = data.trade;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const trade = t0 as Record<string, unknown>;
  const rawTxHash = (data._txHash as string | null ?? trade.transactionHash as string | null) || "";
  let t1;
  if ($[2] !== rawTxHash) {
    t1 = /^0x[a-fA-F0-9]{64}$/.test(rawTxHash) ? rawTxHash : "";
    $[2] = rawTxHash;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const txHash = t1;
  const isBuy = side === "BUY";
  const sideColor = isBuy ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400";
  const amount = Math.round(price * size * 100) / 100;
  const explorerUrl = txHash ? `https://polygonscan.com/tx/${txHash}` : "";
  const t2 = isBuy ? "success" : "destructive";
  let t3;
  if ($[4] !== side || $[5] !== t2) {
    t3 = <Badge variant={t2}>{side}</Badge>;
    $[4] = side;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== outcome) {
    t4 = <span className="text-ds-text-primary text-sm font-medium">{outcome}</span>;
    $[7] = outcome;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== explorerUrl || $[10] !== txHash) {
    t5 = txHash && <a href={explorerUrl} target="_blank" rel="noopener noreferrer" onClick={_temp4} className="micro-label text-ds-text-disabled hover:text-ds-text-subtle dark:text-ds-text-muted ml-auto inline-flex items-center gap-1 transition-colors dark:hover:text-zinc-300">{formatAddress(txHash)}<IconArrowUpRightOutline18 className="h-3 w-3" /></a>;
    $[9] = explorerUrl;
    $[10] = txHash;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = <div className="flex items-center gap-2">{t3}{t4}{t5}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== size) {
    t7 = size.toFixed(1);
    $[16] = size;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = price * 100;
  let t9;
  if ($[18] !== t8) {
    t9 = t8.toFixed(0);
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t7 || $[21] !== t9) {
    t10 = <span className="label label-xs">{t7} shares @ {t9}¢</span>;
    $[20] = t7;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="label label-xs dark:text-ds-text-subtle text-zinc-300" aria-hidden="true">·</span>;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = `label label-xs ${sideColor}`;
  let t13;
  if ($[24] !== amount) {
    t13 = amount.toFixed(2);
    $[24] = amount;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12 || $[27] !== t13) {
    t14 = <span className={t12}>${t13}</span>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t10 || $[30] !== t14) {
    t15 = <div className="flex items-center gap-1.5 tabular-nums">{t10}{t11}{t14}</div>;
    $[29] = t10;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== props || $[33] !== t15 || $[34] !== t6) {
    t16 = <PickerRow {...props}>{t6}{t15}</PickerRow>;
    $[32] = props;
    $[33] = t15;
    $[34] = t6;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
}

/** Compact row for Slack reaction events */
function _temp4(e) {
  return e.stopPropagation();
}
export function ReactionRow(props) {
  const $ = _c(9);
  const {
    data
  } = props;
  const preview = data._messagePreview as string ?? "";
  const channel = data._channelName as string ?? "";
  const user = data._userName as string ?? "";
  const t0 = channel ? ` in #${channel}` : "";
  const t1 = user ? ` by ${user}` : "";
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = <span className="label-xs-medium">Reacted message{t0}{t1}</span>;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== preview) {
    t3 = preview && <span className="text-ds-text-primary line-clamp-2 text-sm">{preview}</span>;
    $[3] = preview;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== props || $[6] !== t2 || $[7] !== t3) {
    t4 = <PickerRow {...props} align="start">{t2}{t3}</PickerRow>;
    $[5] = props;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

/** Compact row for Slack channel created events */
export function ChannelRow(props) {
  const $ = _c(10);
  const {
    data
  } = props;
  const name = data._channelName as string ?? "";
  const members = data._memberCount as number ?? 0;
  const created = data._createdAt as string ?? "";
  let t0;
  if ($[0] !== name) {
    t0 = <span className="text-ds-text-primary text-sm">#{name}</span>;
    $[0] = name;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = members !== 1 ? "s" : "";
  const t2 = created ? ` · created ${created}` : "";
  let t3;
  if ($[2] !== members || $[3] !== t1 || $[4] !== t2) {
    t3 = <span className="text-ds-text-disabled dark:text-ds-text-muted text-xs tabular-nums">{members} member{t1}{t2}</span>;
    $[2] = members;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== props || $[7] !== t0 || $[8] !== t3) {
    t4 = <PickerRow {...props}>{t0}{t3}</PickerRow>;
    $[6] = props;
    $[7] = t0;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/** Compact row for Shopify order events */
export function ShopifyOrderRow(props) {
  const $ = _c(21);
  const {
    data
  } = props;
  const order = data.order as Record<string, unknown> | undefined;
  const lineItems = (order?.line_items ?? order?.lineItems) as Record<string, unknown>[] | undefined;
  const orderName = data._orderName as string || order?.name as string || "";
  const rawPrice = (order?.total_price ?? order?.totalPrice) as string | undefined;
  const totalPrice = data._totalPrice as string || (rawPrice ? `$${rawPrice}` : "");
  const productTitle = data._productTitle as string || lineItems?.[0]?.title as string || "";
  let t0;
  if ($[0] !== data._status || $[1] !== order?.financialStatus || $[2] !== order?.financial_status) {
    t0 = data._status as string || ((order?.financial_status ?? order?.financialStatus) as string)?.toLowerCase() || "";
    $[0] = data._status;
    $[1] = order?.financialStatus;
    $[2] = order?.financial_status;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const status = t0;
  const statusVariant = status === "paid" ? "success" : status === "refunded" || status === "voided" ? "destructive" : status === "pending" || status === "authorized" || status === "partially_paid" ? "warning" : "default";
  let t1;
  if ($[4] !== orderName) {
    t1 = <span className="text-ds-text-primary text-sm font-medium">{orderName}</span>;
    $[4] = orderName;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== totalPrice) {
    t2 = totalPrice && <Badge variant="outline">{totalPrice}</Badge>;
    $[6] = totalPrice;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== status || $[9] !== statusVariant) {
    t3 = status && <Badge variant={statusVariant}>{status}</Badge>;
    $[8] = status;
    $[9] = statusVariant;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3) {
    t4 = <div className="flex items-center gap-2">{t1}{t2}{t3}</div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== productTitle) {
    t5 = productTitle && <span className="text-ds-text-disabled dark:text-ds-text-muted text-xs">{productTitle}</span>;
    $[15] = productTitle;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== props || $[18] !== t4 || $[19] !== t5) {
    t6 = <PickerRow {...props}>{t4}{t5}</PickerRow>;
    $[17] = props;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}

/** Coinbase Commerce network name → EVM chainId */
const COINBASE_NETWORK_TO_CHAIN_ID: Record<string, number> = {
  base: 8453,
  "base-sepolia": 84532,
  ethereum: 1,
  mainnet: 1,
  polygon: 137,
  optimism: 10,
  arbitrum: 42161
};

/** Compact row for Coinbase payment link events */
export function CoinbasePaymentRow(props) {
  const $ = _c(37);
  const {
    data
  } = props;
  let t0;
  if ($[0] !== data.paymentLink) {
    t0 = data.paymentLink ?? {};
    $[0] = data.paymentLink;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const paymentLink = t0 as Record<string, unknown>;
  const rawAmount = paymentLink.amount as string ?? "";
  const t1 = paymentLink.currency as string || "";
  let t2;
  if ($[2] !== t1) {
    t2 = t1.toUpperCase();
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const currency = t2;
  const network = (paymentLink.network as string || data._network as string || "").toLowerCase();
  const t3 = paymentLink.status as string || data._status as string || "";
  let t4;
  if ($[4] !== t3) {
    t4 = t3.toLowerCase();
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const status = t4;
  const description = paymentLink.description as string || data._description as string || "";
  const chainId = COINBASE_NETWORK_TO_CHAIN_ID[network];
  const {
    data: chainInfo
  } = useChainInfo(chainId);
  let t5;
  if ($[6] !== chainId || $[7] !== chainInfo?.iconUrl) {
    t5 = (chainId ? getChainIconUrl(chainId) : null) ?? chainInfo?.iconUrl ?? null;
    $[6] = chainId;
    $[7] = chainInfo?.iconUrl;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const chainIconUrl = t5;
  const chainName = chainInfo?.name ?? (network ? network.charAt(0).toUpperCase() + network.slice(1) : "");
  const statusVariant = status === "completed" ? "success" : status === "failed" ? "destructive" : status === "expired" ? "default" : "warning";
  const t6 = currency || "USDC";
  let t7;
  if ($[9] !== chainIconUrl || $[10] !== chainId || $[11] !== chainName || $[12] !== t6) {
    t7 = <TokenWithChainIcon symbol={t6} chainId={chainId} chainIconUrl={chainIconUrl} chainName={chainName} tokenSize={28} chainIconSize={14} />;
    $[9] = chainIconUrl;
    $[10] = chainId;
    $[11] = chainName;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== rawAmount) {
    t8 = <span className="text-ds-text-primary text-sm font-semibold tabular-nums">{rawAmount}</span>;
    $[14] = rawAmount;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = chainName ? ` on ${chainName}` : "";
  let t10;
  if ($[16] !== currency || $[17] !== t9) {
    t10 = <span className="text-ds-text-muted min-w-0 truncate text-sm">{currency}{t9}</span>;
    $[16] = currency;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== status || $[20] !== statusVariant) {
    t11 = status && <Badge variant={statusVariant}>{status}</Badge>;
    $[19] = status;
    $[20] = statusVariant;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t8) {
    t12 = <div className="flex items-center gap-2">{t8}{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== description) {
    t13 = description && <span className="text-ds-text-disabled dark:text-ds-text-muted mt-0.5 block truncate text-xs">{description}</span>;
    $[26] = description;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t12 || $[29] !== t13) {
    t14 = <div className="min-w-0 flex-1">{t12}{t13}</div>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14 || $[32] !== t7) {
    t15 = <div className="flex min-w-0 items-center gap-3">{t7}{t14}</div>;
    $[31] = t14;
    $[32] = t7;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== props || $[35] !== t15) {
    t16 = <PickerRow {...props}>{t15}</PickerRow>;
    $[34] = props;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  return t16;
}

/** Compact row for Slack user added events */
export function UserRow(props) {
  const $ = _c(23);
  const {
    data
  } = props;
  const displayName = data._displayName as string ?? "";
  const userName = data._userName as string ?? "";
  const isAdmin = data._isAdmin as boolean ?? false;
  const avatarUrl = data._avatarUrl as string ?? "";
  let t0;
  if ($[0] !== displayName || $[1] !== userName) {
    t0 = (displayName || userName).charAt(0).toUpperCase() || "?";
    $[0] = displayName;
    $[1] = userName;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const initial = t0;
  let t1;
  if ($[3] !== avatarUrl || $[4] !== displayName || $[5] !== initial) {
    t1 = avatarUrl ? <img src={avatarUrl} alt={displayName} className="h-8 w-8 flex-shrink-0 rounded-full object-cover" /> : <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium">{initial}</div>;
    $[3] = avatarUrl;
    $[4] = displayName;
    $[5] = initial;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== displayName) {
    t2 = <span className="body-sm-semibold truncate">{displayName}</span>;
    $[7] = displayName;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== userName) {
    t3 = userName && <span className="caption-xs truncate">@{userName}</span>;
    $[9] = userName;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== isAdmin) {
    t4 = isAdmin && <Badge variant="warning">admin</Badge>;
    $[11] = isAdmin;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = <div className="flex min-w-0 flex-1 items-center gap-1.5">{t2}{t3}{t4}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t1 || $[18] !== t5) {
    t6 = <div className="flex min-w-0 items-center gap-2.5">{t1}{t5}</div>;
    $[17] = t1;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== props || $[21] !== t6) {
    t7 = <PickerRow {...props}>{t6}</PickerRow>;
    $[20] = props;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}

/** Split a Slack message preview into text + @mention tokens, rendering mentions in blue. */
function renderMentionPreview(text: string): ReactNode[] {
  // Matches @word, @word.eth, @Mike, @here, etc. Stops at whitespace or punctuation
  // that can't be part of a display name.
  const parts = text.split(/(@[A-Za-z0-9._-]+)/g);
  return parts.map((part, i) => part.startsWith("@") ? <span key={i} className="text-blue-600 dark:text-blue-400">
        {part}
      </span> : <span key={i}>{part}</span>);
}

/** Compact row for Slack user mention events */
export function MentionRow(props) {
  const $ = _c(20);
  const {
    data
  } = props;
  const preview = data._messagePreview as string ?? "";
  const channel = data._channelName as string ?? "";
  const user = data._userName as string ?? "";
  const avatarUrl = data._avatarUrl as string ?? "";
  let t0;
  if ($[0] !== user) {
    t0 = user.charAt(0).toUpperCase() || "?";
    $[0] = user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const initial = t0;
  let t1;
  if ($[2] !== avatarUrl || $[3] !== initial || $[4] !== user) {
    t1 = avatarUrl ? <img src={avatarUrl} alt={user} className="h-8 w-8 flex-shrink-0 rounded-full object-cover" /> : <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium">{initial}</div>;
    $[2] = avatarUrl;
    $[3] = initial;
    $[4] = user;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = channel ? ` in #${channel}` : "";
  const t3 = user ? ` by ${user}` : "";
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="label-xs-medium">Mention{t2}{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== preview) {
    t5 = preview && <span className="text-ds-text-primary line-clamp-2 text-sm">{renderMentionPreview(preview)}</span>;
    $[9] = preview;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div className="flex min-w-0 flex-1 flex-col justify-center">{t4}{t5}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t1 || $[15] !== t6) {
    t7 = <div className="flex min-w-0 items-center gap-2.5">{t1}{t6}</div>;
    $[14] = t1;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== props || $[18] !== t7) {
    t8 = <PickerRow {...props} align="start">{t7}</PickerRow>;
    $[17] = props;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
export function TweetRow(t0) {
  const $ = _c(39);
  const {
    data,
    value,
    selected,
    onSelect
  } = t0;
  const text = data._text as string ?? "";
  const authorUsername = data._authorUsername as string || "unknown";
  const authorName = data._authorName as string || authorUsername;
  const pfpUrl = data._authorProfilePicture as string || "";
  const isVerified = data._authorIsBlueVerified as boolean ?? false;
  const likes = data._likeCount as number ?? 0;
  const retweets = data._retweetCount as number ?? 0;
  let t1;
  if ($[0] !== authorName || $[1] !== pfpUrl) {
    t1 = pfpUrl ? <img src={pfpUrl} alt={authorName} className="h-8 w-8 flex-shrink-0 rounded-full object-cover" /> : <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium">{authorName.charAt(0).toUpperCase()}</div>;
    $[0] = authorName;
    $[1] = pfpUrl;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== authorName) {
    t2 = <span className="text-ds-text-primary truncate text-sm font-semibold leading-5">{authorName}</span>;
    $[3] = authorName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isVerified) {
    t3 = isVerified && <IconCircleCheckOutline18 className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />;
    $[5] = isVerified;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== authorUsername) {
    t4 = <span className="text-ds-text-disabled dark:text-ds-text-muted min-w-0 truncate text-xs leading-5">@{authorUsername}</span>;
    $[7] = authorUsername;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <div className="relative -top-0.5 flex min-w-0 items-center gap-1.5">{t2}{t3}{t4}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== text) {
    t6 = <p className="text-ds-text-subtle dark:text-ds-text-disabled mt-0.5 line-clamp-2 text-sm leading-6">{text}</p>;
    $[13] = text;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== likes) {
    t7 = likes.toLocaleString();
    $[15] = likes;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <span className="text-ds-text-disabled dark:text-ds-text-muted text-xs">{t7} likes</span>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== retweets) {
    t9 = retweets.toLocaleString();
    $[19] = retweets;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <span className="text-ds-text-disabled dark:text-ds-text-muted text-xs">{t9} retweets</span>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8) {
    t11 = <div className="mt-1 flex items-center gap-3">{t8}{t10}</div>;
    $[23] = t10;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t5 || $[28] !== t6) {
    t12 = <div className="min-w-0 flex-1">{t5}{t6}{t11}</div>;
    $[26] = t11;
    $[27] = t5;
    $[28] = t6;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t1 || $[31] !== t12) {
    t13 = <div className="flex min-w-0 items-start gap-2.5">{t1}{t12}</div>;
    $[30] = t1;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== data || $[34] !== onSelect || $[35] !== selected || $[36] !== t13 || $[37] !== value) {
    t14 = <PickerRow data={data} value={value} selected={selected} onSelect={onSelect} align="start">{t13}</PickerRow>;
    $[33] = data;
    $[34] = onSelect;
    $[35] = selected;
    $[36] = t13;
    $[37] = value;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
}
export function ExchangePairRow(t0) {
  const $ = _c(14);
  const {
    data,
    value,
    selected,
    onSelect
  } = t0;
  const symbol = data.symbol as string ?? "";
  const baseAsset = data.baseAsset as string ?? "";
  const quoteAsset = data.quoteAsset as string ?? "";
  let t1;
  if ($[0] !== symbol) {
    t1 = <span className="body-sm-semibold">{symbol}</span>;
    $[0] = symbol;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== baseAsset || $[3] !== quoteAsset) {
    t2 = <p className="caption-xs">{baseAsset} / {quoteAsset}</p>;
    $[2] = baseAsset;
    $[3] = quoteAsset;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div>{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== data || $[9] !== onSelect || $[10] !== selected || $[11] !== t3 || $[12] !== value) {
    t4 = <PickerRow data={data} value={value} selected={selected} onSelect={onSelect}>{t3}</PickerRow>;
    $[8] = data;
    $[9] = onSelect;
    $[10] = selected;
    $[11] = t3;
    $[12] = value;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export function ModeToggle(t0) {
  const $ = _c(6);
  const {
    mode,
    onModeChange
  } = t0;
  let t1;
  if ($[0] !== onModeChange) {
    t1 = value => onModeChange(value as "recent" | "paste");
    $[0] = onModeChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TabsList className="w-fit"><TabsTrigger value="recent">Recent events</TabsTrigger><TabsTrigger value="paste">Paste transaction</TabsTrigger></TabsList>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== mode || $[4] !== t1) {
    t3 = <Tabs value={mode} onValueChange={t1} size="sm">{t2}</Tabs>;
    $[3] = mode;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export type TransferMode = "recent" | "custom";
export function TransferModeToggle(t0) {
  const $ = _c(6);
  const {
    mode,
    onModeChange
  } = t0;
  let t1;
  if ($[0] !== onModeChange) {
    t1 = value => onModeChange(value as TransferMode);
    $[0] = onModeChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TabsList className="w-fit"><TabsTrigger value="recent">Recent activity</TabsTrigger><TabsTrigger value="custom">Custom amount</TabsTrigger></TabsList>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== mode || $[4] !== t1) {
    t3 = <Tabs value={mode} onValueChange={t1} size="sm">{t2}</Tabs>;
    $[3] = mode;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function FarcasterReactionTypeToggle(t0) {
  const $ = _c(7);
  const {
    value,
    onChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="space-y-0.5"><span className="field-label">Reaction type</span><p className="text-ds-text-muted text-sm">Pick the reaction to simulate, then choose the cast it targets.</p></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onChange) {
    t2 = nextValue => onChange(nextValue as FarcasterReactionType);
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <TabsList><TabsTrigger value="like">Like</TabsTrigger><TabsTrigger value="recast">Recast</TabsTrigger></TabsList>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== value) {
    t4 = <div className="space-y-3">{t1}<Tabs value={value} onValueChange={t2} size="sm">{t3}</Tabs></div>;
    $[4] = t2;
    $[5] = value;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function formatLocalTime(raw: string): string {
  try {
    return new Date(raw).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return raw;
  }
}

/** Row for Unusual Whales market news headlines */
export function NewsHeadlineRow(props) {
  const $ = _c(25);
  const {
    data
  } = props;
  const headline = data.headline as string ?? "";
  const timestamp = data.timestamp as string ?? "";
  const source = data.source as string ?? "";
  const isMajor = data.isMajor as boolean ?? false;
  let t0;
  if ($[0] !== data.tickers) {
    t0 = data.tickers as string[] ?? [];
    $[0] = data.tickers;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const tickers = t0;
  let t1;
  if ($[2] !== headline) {
    t1 = <span className="text-ds-text-primary text-sm leading-snug">{headline}</span>;
    $[2] = headline;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== timestamp) {
    t2 = formatLocalTime(timestamp);
    $[4] = timestamp;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <span className="micro-label text-ds-text-disabled">{t2}</span>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== source) {
    t4 = source && <span className="micro-label text-ds-text-muted">{source}</span>;
    $[8] = source;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isMajor) {
    t5 = isMajor && <Badge variant="default">Major</Badge>;
    $[10] = isMajor;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== tickers) {
    t6 = tickers.length > 0 && <span className="micro-label text-ds-text-muted">{tickers.join(", ")}</span>;
    $[12] = tickers;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    t7 = <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">{t3}{t4}{t5}{t6}</div>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t1 || $[20] !== t7) {
    t8 = <div className="min-w-0 space-y-1">{t1}{t7}</div>;
    $[19] = t1;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== props || $[23] !== t8) {
    t9 = <PickerRow {...props} align="start">{t8}</PickerRow>;
    $[22] = props;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}

/** Row for Unusual Whales options flow alerts */
export function FlowAlertRow(props) {
  const $ = _c(25);
  const {
    data
  } = props;
  const ticker = data.ticker as string ?? "";
  const optionType = data.optionType as string ?? "";
  const totalPremium = data.totalPremium as number ?? 0;
  const hasSweep = data.hasSweep as boolean ?? false;
  const optionChain = data.optionChain as string ?? "";
  const startTime = data.startTime as string ?? "";
  const isCall = optionType === "call";
  const typeColor = isCall ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400";
  const premiumStr = totalPremium >= 1000000 ? `$${(totalPremium / 1000000).toFixed(1)}M` : totalPremium >= 1000 ? `$${(totalPremium / 1000).toFixed(0)}K` : `$${totalPremium.toFixed(0)}`;
  let t0;
  if ($[0] !== ticker) {
    t0 = <span className="text-ds-text-primary text-sm font-medium">{ticker}</span>;
    $[0] = ticker;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = `label label-xs font-semibold uppercase ${typeColor}`;
  let t2;
  if ($[2] !== optionType || $[3] !== t1) {
    t2 = <span className={t1}>{optionType}</span>;
    $[2] = optionType;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== premiumStr) {
    t3 = <span className="label label-xs text-ds-text-muted">{premiumStr}</span>;
    $[5] = premiumStr;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== hasSweep) {
    t4 = hasSweep && <Badge variant="default">Sweep</Badge>;
    $[7] = hasSweep;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t0 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex items-center gap-2">{t0}{t2}{t3}{t4}</div>;
    $[9] = t0;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== optionChain) {
    t6 = <span className="micro-label text-ds-text-disabled truncate">{optionChain}</span>;
    $[14] = optionChain;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== startTime) {
    t7 = startTime && <><span className="micro-label text-ds-text-disabled" aria-hidden="true">·</span><span className="micro-label text-ds-text-disabled">{formatLocalTime(startTime)}</span></>;
    $[16] = startTime;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <div className="flex items-center gap-2">{t6}{t7}</div>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== props || $[22] !== t5 || $[23] !== t8) {
    t9 = <PickerRow {...props}>{t5}{t8}</PickerRow>;
    $[21] = props;
    $[22] = t5;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}